// Componente de firma manuscrita digital con canvas
// Usa signature_pad v5 (Pointer Events API) — compatible con ratón, táctil y stylus

import { useRef, useEffect, useState, useCallback } from 'react';
import SignaturePadLib from 'signature_pad';
import { Button } from '@/components/ui/button';
import { ArrowUturnLeftIcon, TrashIcon } from '@heroicons/react/24/outline';

interface SignaturePadProps {
  /** Callback cuando la firma cambia (null si se borra) */
  onSignatureChange: (signatureBase64: string | null) => void;
  /** Ancho del canvas (responsive por defecto) */
  width?: number;
  /** Alto del canvas */
  height?: number;
  /** Color del trazo */
  penColor?: string;
  /** Grosor mínimo del trazo */
  minWidth?: number;
  /** Grosor máximo del trazo */
  maxWidth?: number;
  /** Texto hint que se muestra cuando el canvas está vacío */
  hintText?: string;
  /** Texto secundario de instrucción bajo el hint principal */
  drawHintText?: string;
  /** Etiqueta del botón Deshacer */
  undoLabel?: string;
  /** Etiqueta del botón Limpiar */
  clearLabel?: string;
  /** Texto de estado cuando no hay firma */
  noSignatureLabel?: string;
  /** Texto de estado cuando la firma es válida */
  validSignatureLabel?: string;
  /** Texto de estado cuando la firma es demasiado corta */
  shortSignatureLabel?: string;
  /** Sugerencia de rotación en móvil */
  rotateHintText?: string;
  /** Desactivar interacción */
  disabled?: boolean;
  /** Clase CSS adicional para el contenedor */
  className?: string;
}

/** Número mínimo de puntos para considerar una firma válida */
const MIN_POINTS_FOR_VALID_SIGNATURE = 20;

export function SignaturePad({
  onSignatureChange,
  height = 200,
  penColor = '#1a1a2e',
  minWidth = 0.5,
  maxWidth = 2.5,
  hintText = 'Firme aquí con el dedo o ratón, igual que en su pasaporte',
  drawHintText = '✍️ Toque o haga clic y dibuje su firma',
  undoLabel = 'Deshacer',
  clearLabel = 'Limpiar',
  noSignatureLabel = 'Sin firma',
  validSignatureLabel = '✓ Firma válida',
  shortSignatureLabel = 'Firma demasiado corta...',
  rotateHintText = '📱 Gire el dispositivo en horizontal para más espacio de firma',
  disabled = false,
  className = '',
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const signaturePadRef = useRef<SignaturePadLib | null>(null);
  const onSignatureChangeRef = useRef(onSignatureChange);
  const [isEmpty, setIsEmpty] = useState(true);
  const [pointCount, setPointCount] = useState(0);

  // Mantener ref actualizada sin re-crear el pad
  useEffect(() => {
    onSignatureChangeRef.current = onSignatureChange;
  }, [onSignatureChange]);

  // Función para dimensionar el canvas para HiDPI
  const setupCanvasDimensions = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const containerWidth = container.offsetWidth;

    canvas.width = containerWidth * ratio;
    canvas.height = height * ratio;
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
    }
  }, [height]);

  // Inicializar SignaturePad — canvas se dimensiona ANTES de crear la instancia
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // 1. Dimensionar canvas ANTES de crear el pad (crítico para coordenadas correctas)
    setupCanvasDimensions();

    // 2. Crear instancia de signature_pad con Pointer Events (ratón + táctil + stylus)
    const pad = new SignaturePadLib(canvas, {
      penColor,
      minWidth,
      maxWidth,
      backgroundColor: 'rgba(255, 255, 255, 0)',
      throttle: 16,
      // signature_pad v5 usa Pointer Events API por defecto
      // Esto unifica mousedown/mousemove/mouseup + touchstart/touchmove/touchend
    });

    if (disabled) {
      pad.off();
    }

    signaturePadRef.current = pad;

    // 3. Escuchar fin de trazo usando la API nativa de signature_pad v5
    const handleEndStroke = () => {
      const data = pad.toData();
      const totalPoints = data.reduce((acc, stroke) => acc + stroke.points.length, 0);
      setPointCount(totalPoints);
      setIsEmpty(pad.isEmpty());

      if (totalPoints >= MIN_POINTS_FOR_VALID_SIGNATURE) {
        const base64 = pad.toDataURL('image/png');
        onSignatureChangeRef.current(base64);
      } else {
        onSignatureChangeRef.current(null);
      }
    };

    pad.addEventListener('endStroke', handleEndStroke);

    // 4. Manejar resize — guardar datos, redimensionar, restaurar
    const handleResize = () => {
      const data = pad.toData();
      setupCanvasDimensions();
      pad.clear(); // Necesario para resetear estado interno tras resize
      if (data.length > 0) {
        pad.fromData(data);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      pad.removeEventListener('endStroke', handleEndStroke);
      window.removeEventListener('resize', handleResize);
      pad.off();
    };
     
  }, [disabled, penColor, minWidth, maxWidth, setupCanvasDimensions]);

  // Limpiar firma
  const handleClear = () => {
    const pad = signaturePadRef.current;
    if (pad) {
      pad.clear();
      setIsEmpty(true);
      setPointCount(0);
      onSignatureChangeRef.current(null);
    }
  };

  // Deshacer último trazo
  const handleUndo = () => {
    const pad = signaturePadRef.current;
    if (pad) {
      const data = pad.toData();
      if (data.length > 0) {
        data.pop();
        pad.fromData(data);
        const totalPoints = data.reduce((acc, stroke) => acc + stroke.points.length, 0);
        setPointCount(totalPoints);
        setIsEmpty(data.length === 0);

        if (totalPoints >= MIN_POINTS_FOR_VALID_SIGNATURE) {
          const base64 = pad.toDataURL('image/png');
          onSignatureChangeRef.current(base64);
        } else {
          onSignatureChangeRef.current(null);
        }
      }
    }
  };

  const isValidSignature = pointCount >= MIN_POINTS_FOR_VALID_SIGNATURE;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Canvas container */}
      <div
        ref={containerRef}
        className="relative w-full border-2 border-dashed border-gray-300 rounded-xl bg-white overflow-hidden transition-colors"
        style={{
          borderColor: disabled
            ? '#d1d5db'
            : isValidSignature
            ? '#22c55e'
            : isEmpty
            ? '#d1d5db'
            : '#f59e0b',
        }}
      >
        {/* Hint text (visible cuando el canvas está vacío) */}
        {isEmpty && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-10">
            <p className="text-gray-400 text-base font-medium text-center px-4">
              {hintText}
            </p>
            <p className="text-gray-300 text-sm mt-1">
              {drawHintText}
            </p>
          </div>
        )}

        {/* Línea de firma (guía visual) */}
        <div
          className="absolute bottom-10 left-8 right-8 border-b border-gray-200 pointer-events-none z-10"
          aria-hidden="true"
        />

        <canvas
          ref={canvasRef}
          className={`w-full touch-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-crosshair'}`}
          style={{ height: `${height}px` }}
        />
      </div>

      {/* Controles */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleUndo}
            disabled={disabled || isEmpty}
            className="text-gray-600"
          >
            <ArrowUturnLeftIcon className="w-4 h-4 mr-1" />
            {undoLabel}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClear}
            disabled={disabled || isEmpty}
            className="text-red-600 hover:text-red-700 hover:border-red-300"
          >
            <TrashIcon className="w-4 h-4 mr-1" />
            {clearLabel}
          </Button>
        </div>

        {/* Indicador de estado */}
        <div className="text-sm">
          {isEmpty ? (
            <span className="text-gray-400">{noSignatureLabel}</span>
          ) : isValidSignature ? (
            <span className="text-green-600 font-medium">{validSignatureLabel}</span>
          ) : (
            <span className="text-amber-500">{shortSignatureLabel}</span>
          )}
        </div>
      </div>

      {/* Sugerencia de orientación en móvil */}
      <div className="md:hidden text-center">
        <p className="text-xs text-gray-400">
          {rotateHintText}
        </p>
      </div>
    </div>
  );
}
